import { useContext, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Menu from './Menu'
import { Typography, Skeleton } from '@mui/material'
import { Apartment, TaskAlt, Approval, AssignmentInd, Dangerous, FactCheck, Factory, Healing, People, Science, MonitorHeart, HealthAndSafety, OpenInNewRounded, Troubleshoot, Contacts, Stream } from '@mui/icons-material'
import MenuItem from './MenuItem'
import MenuDivider from './MenuDivider'
import { ROLES } from '../auth/role'
import RoleContext from '../auth/RoleContext'
import UserContext from '../auth/UserContext'
import CacheContext, { CACHE_KEY } from '../cache/CacheContext'
import HelpDialog from '../help/HelpDialog'
import AssessmentGuidelines from '../help/AssessmentGuidelines'
import RiskMatrix from '../help/RiskMatrix'
import { getForumEmbedConfig } from '../communityHub/utils'
import FeatureFlagsContext from '../config/FeatureFlagsContext'
import { getSiteConfig } from '../community/siteProvider'

export default function SidebarMenu(props = {}) {
  window.scrollTo(0, 0)
  const location = useLocation()
  const roleDetails = useContext(RoleContext)
  const userDetails = useContext(UserContext)
  const cacheContext = useContext(CacheContext)
  const ff = useContext(FeatureFlagsContext)

  const [ownershipUuid, setOwnershipUuid] = useState(null)
  const [ownership, setOwnership] = useState({})
  const [manufacturerUuid, setManufacturerUuid] = useState(null)
  const [manufacturer, setManufacturer] = useState({})
  const [manufacturerProfile, setManufacturerProfile] = useState({})
  const [vivaCommunityLinkPrefix, setVivaCommunityLinkPrefix] = useState(null)
  
  const {isAdminUser, isQualityManager, isSafetyManager, isSupplyChainManager, isQseMember, isBottlerUser, isInternalUser, isImManager, isSiteManager, isSupplyChainBottlerUser} = roleDetails

  const isNorthstarBottler = () => {
    if (isBottlerUser()) {
      return userDetails.userProfile.manufacturers?.some((m) => {
        return m.isNorthstar
      })
    }
    return false
  }

  useEffect(() => {
    const { vivaCommunity } = getSiteConfig()
    if (vivaCommunity) setVivaCommunityLinkPrefix(vivaCommunity['communityLinkPrefix'])
  }, [])

  useEffect(() => {
    if (cacheContext) {
    const mfr = cacheContext.getItem(CACHE_KEY.MANUFACTURER)
    if (mfr) {
      setManufacturerUuid(mfr.uuid)
      setManufacturer(mfr)
      const mfrProfile = cacheContext.getItem(CACHE_KEY.MFR_PROFILE)
      if(mfrProfile) {
        setManufacturerProfile(mfrProfile)
      }
    }
    const owner = cacheContext.getItem(CACHE_KEY.OWNERSHIP)
    if (owner) {
      setOwnershipUuid(owner.uuid)
      setOwnership(owner)
    }
    }
  }, [cacheContext, cacheContext.cacheMap])


  const animationSpeed = 200

  const isExpandedLocation = (pathRegex, flags = undefined) => {
    const t = new RegExp(pathRegex, flags)
    const match = t.test(location.pathname)
    return match
  }
  const safetyForumEmbedConfig = getForumEmbedConfig()

  return (
    <Menu>
      { ff.featureFlags.communityHub 
        && userDetails.hasGroupMembership([
          'NAOU QSE Safety Hub Manager',
          'NAOU QSE Safety Hub Users',
          'NAOU QSE Community Hub Users',
          'NAOU QSE Quality Hub Manager',
          'NAOU QSE Sustainability Hub Manager'
        ]) 
        && 
        (isInternalUser() || isBottlerUser())
        &&
          <>
          <MenuItem id='community-hub-menu' href='/qse-community-hub' title="QSE Community Hub" icon={<Stream />} />
          <MenuDivider />
          </>
      }
      {roleDetails.hasAnyRole() &&
        <>
          {(isQualityManager() || isSafetyManager() || isBottlerUser()) && <>
            <MenuItem activePaths={['/my-assessments']} href="/my-assessments" title="My Assessments" icon={<AssignmentInd />} />
            <MenuItem activePaths={['/corrective-action-plan/my-list']} href="/corrective-action-plan/my-list" title="My Corrective Actions" icon={<Approval />} />
          </>}

          {!roleDetails.hasRole([ROLES.IM_VIEWER, ROLES.BOTTLER_SUPPLY_CHAIN]) && <>
            <MenuItem id='ownership-menu' animationSpeed={animationSpeed} expanded={isExpandedLocation('/ownership/edit/.*')} activePaths={['ownership/list', '/ownership/edit']} title="Ownerships" href="/ownership/list" icon={<Apartment />}>
              <MenuItem wrap title={
                ownership?.ownershipName
                  ? <Typography fontWeight='800' variant='body2'>{ownership?.ownershipName}</Typography>
                  : <Skeleton sx={{ height: '18px', width: '200px' }} />
              } />
              <MenuItem activePaths={['/ownership/edit/[^/]*/details']} href={`/ownership/edit/${ownershipUuid}/details`} title={<Typography fontWeight='400' variant='body2'>Ownership Details</Typography>} />
              <MenuItem activePaths={['/ownership/edit/[^/]*/contacts']} href={`/ownership/edit/${ownershipUuid}/contacts`} title={<Typography fontWeight='400' variant='body2'>Contacts</Typography>} />
              {ff.featureFlags.ownershipTargets && (isInternalUser() || roleDetails.hasRole([ROLES.BOTTLER_CORP])) &&
                <MenuItem activePaths={['/ownership/edit/[^/]*/targets']} href={`/ownership/edit/${ownershipUuid}/targets`} title={<Typography fontWeight='400' variant='body2'>Metric Targets</Typography>} />
              }
            </MenuItem>
          </>}

          {manufacturer && !roleDetails.hasRole([ROLES.IM_VIEWER]) && <>
            <MenuItem id='manufacturerProfile-menu' animationSpeed={animationSpeed} expanded={isExpandedLocation('/manufacturer/edit/.*')} activePaths={['manufacturer/list', '/manufacturer/edit']} title="Facilities" href="/manufacturer/list" icon={<Factory />}>
              <MenuItem wrap title={
                manufacturer?.manufacturerName
                  ? <Typography fontWeight='800' variant='body2'>{manufacturer?.manufacturerName}&nbsp;</Typography>
                  : <Skeleton sx={{ width: '200px' }} />
              } />
              <MenuItem activePaths={['/manufacturer/edit/[^/]*/details']} href={`/manufacturer/edit/${manufacturerUuid}/details`} title={<Typography fontWeight='400' variant='body2'>Facility Details</Typography>} />
              <MenuItem activePaths={['/manufacturer/edit/[^/]*/contacts']} href={`/manufacturer/edit/${manufacturerUuid}/contacts`} title={<Typography fontWeight='400' variant='body2'>Contacts</Typography>} />
              {['Producing Plant', 'Co-packer'].includes(manufacturer?.type) && manufacturerProfile?.id &&
               (isInternalUser() || isBottlerUser()) &&
                <>
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/assessment-contacts']} href={`/manufacturer/edit/${manufacturerUuid}/assessment-contacts`} title={<Typography fontWeight='400' variant='body2'>Assessment Contacts</Typography>} />
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/profile']} href={`/manufacturer/edit/${manufacturerUuid}/profile`} title={<Typography fontWeight='400' variant='body2'>Manufacturer Profile</Typography>} />
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/products-processes']} href={`/manufacturer/edit/${manufacturerUuid}/products-processes`} title={<Typography fontWeight='400' variant='body2'>Products & Processes</Typography>} />
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/raw-materials']} href={`/manufacturer/edit/${manufacturerUuid}/raw-materials`} title={<Typography fontWeight='400' variant='body2'>Raw Materials</Typography>} />
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/comanufactured-product']} href={`/manufacturer/edit/${manufacturerUuid}/co-manufactured`} title={<Typography fontWeight='400' variant='body2'>Co-Manufactured Products</Typography>} />
                  <MenuItem activePaths={['/manufacturer/edit/[^/]*/business-continuity']} href={`/manufacturer/edit/${manufacturerUuid}/business-continuity`} title={<Typography fontWeight='400' variant='body2'>Business Continuity</Typography>} />
                </>
              }
            </MenuItem>
          </>}
        </>
      }

      {(isInternalUser()) &&
        <MenuItem activePaths={['/facility-authorizations']} href="/facility-authorizations/list" title="Facility Authorizations" icon={<TaskAlt />} />
      }

      {(isQualityManager() || isQseMember() || isBottlerUser()) &&
        <MenuItem activePaths={['/process-capability-submissions']} href="/process-capability-submissions/list" title="Process Capability Data" icon={<Science />} />
      }

      {(isQualityManager() || isQseMember()) && <MenuItem activePaths={['/manufacturer/production-volumes']} href="/manufacturer/production-volumes" title="Production Volume Reporting" icon={<Factory />} />}

      {isNorthstarBottler() &&
        <MenuItem activePaths={['/manufacturer/production-volumes']} href="/manufacturer/production-volumes" title="Production Volume Reporting" icon={<Factory />}
        />}

      {(isQualityManager() || isQseMember() || isImManager() || isSupplyChainManager()) &&
        <>
          {<MenuItem activePaths={['/complaint-trends']} href="/complaint-trends/list" title="Complaint Trends" icon={<Troubleshoot />} />}
        </>
      }

      {(isInternalUser() || isBottlerUser() || roleDetails.hasRole([ROLES.IM_MANAGER, ROLES.IM_VIEWER])) &&
        <MenuItem activePaths={['/incident-management']} href="/incident-management/list" 
          title={isBottlerUser() ? "Incident Management" : "IM Logs"} 
          icon={<HealthAndSafety />} 
        />
      }

      {!roleDetails.hasRole([ROLES.IM_MANAGER, ROLES.IM_VIEWER,ROLES.SUPPLY_CHAIN_MGR]) && <MenuDivider />}

      {(isQualityManager() || isQseMember() || isImManager())
      && <MenuItem activePaths={['/fitness-indexes']} href="/fitness-indexes" title="Fitness Indexes" icon={<MonitorHeart />} />
      }

      {(isInternalUser()) && (!roleDetails.hasRole([ROLES.SUPPLY_CHAIN_MGR])) &&
        <>
          <MenuItem activePaths={['/assessments']} href="/assessments" title="Facility Assessments" icon={<FactCheck />} />
          <MenuItem activePaths={['/corrective-action-plan/(?!my-list)']} href="/corrective-action-plan/list" title="Facility Corrective Action" icon={<Healing />} />
        </>
      }
      {(isInternalUser() || isBottlerUser()) && (!roleDetails.hasRole([ROLES.SUPPLY_CHAIN_MGR])) && 
        <>
          <li>
            <HelpDialog linkText='Assessment Guidelines' startIcon={<OpenInNewRounded />}>
              <AssessmentGuidelines />
            </HelpDialog>
          </li>
          <li>
            <HelpDialog linkText='Risk Matrix' startIcon={<OpenInNewRounded />} supportZoom={true}>
              <RiskMatrix />
            </HelpDialog>
          </li>
        </>
      }
      { userDetails.hasGroupMembership([
          'NAOU QSE Safety Hub Manager',
          'NAOU QSE Safety Hub Users'
        ]) 
        && 
        (isInternalUser() || isBottlerUser())
        &&
        <>
          { !ff.featureFlags.communityHub &&
          <MenuItem id='safety-hub-menu' href='/safety-community-hub/news' animationSpeed={animationSpeed} expanded={isExpandedLocation('/safety-community-hub*./')} activePaths={[/\/safety-community-hub*./]} title="Safety Community Hub" icon={<Stream />}>
            <MenuItem activePaths={['/safety-community-hub/news']} href={`/safety-community-hub/news`} title={<Typography fontWeight='400' variant='body2'>Community News</Typography>} />
            <MenuItem activePaths={['/safety-community-hub/ou-corner']} href={`/safety-community-hub/ou-corner`} title={<Typography fontWeight='400' variant='body2'>Safety OU Corner</Typography>} />
            <MenuItem activePaths={['/safety-community-hub/resource-library']} href={`/safety-community-hub/resource-library`} title={<Typography fontWeight='400' variant='body2'>Resource Library</Typography>} />
            {(safetyForumEmbedConfig.embedUrl) && <>
              {isBottlerUser() && 
                <MenuItem toBlank={true} href={`${vivaCommunityLinkPrefix}${safetyForumEmbedConfig.communityId}`} title={<Typography fontWeight='400' variant='body2'>Community Forum</Typography>} />
              }
              { !isBottlerUser() &&
                <MenuItem activePaths={['/safety-community-hub/forum']} href={`/safety-community-hub/forum`} title={<Typography fontWeight='400' variant='body2'>Community Forum</Typography>} />
              }
              </>
            }
            {isSiteManager('safety-community-hub') && <MenuItem activePaths={['/safety-community-hub/settings']} href={`/safety-community-hub/settings`} title={<Typography fontWeight='400' variant='body2'>Settings</Typography>} />}
          </MenuItem>
          }
        </>
      }
      {(isInternalUser()) &&
        <>
          <MenuDivider />
          <MenuItem activePaths={['/users']} href="/users/list/" title="User Management" icon={<People />} />
        </>
      }

      {(isQualityManager() || isSupplyChainManager() || (ff.featureFlags.contactMgmtBottler && isBottlerUser()) ) &&
        <MenuItem activePaths={['^/contacts']} href="/contacts/" title="Contacts & Assignments" icon={<Contacts />} />
      }

      {(ff.featureFlags.ownershipTargets && isAdminUser()) && <>
            <MenuItem activePaths={['/manage/targets']} href="/manage/targets" title="Data Management" icon={<Dangerous />} />
      </>
      }
      
      {roleDetails.hasRole([ROLES.ADMIN]) &&
        <MenuItem activePaths={['/app/admin']} href="/app/admin" title="Application Admin" icon={<Dangerous />}
        />}
    </Menu>
  )
}
